import React, { useEffect, useRef, useState } from 'react';
import BackgroundSelectionDialog from '../BackgroundSelectionDialog/BackgroundSelectionDialog';
import ChatWindow from '../ChatWindow/ChatWindow';
import clsx from 'clsx';
import { GalleryView } from '../GalleryView/GalleryView';
import { MobileGalleryView } from '../MobileGalleryView/MobileGalleryView';
import MainParticipant from '../MainParticipant/MainParticipant';
import { makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core';
import { Participant, Room as IRoom } from 'twilio-video';
import { ParticipantAudioTracks } from '../ParticipantAudioTracks/ParticipantAudioTracks';
import ParticipantList from '../ParticipantList/ParticipantList';
import { useAppState } from '../../state';
import useChatContext from '../../hooks/useChatContext/useChatContext';
import useScreenShareParticipant from '../../hooks/useScreenShareParticipant/useScreenShareParticipant';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import useParticipantCount from '../../hooks/useParticipantCount/useParticipantCount';
import CountdownTimer from '../CountdownTimer/CountdownTimer';
import ForceEndButton from '../ForceEndButton/ForceEndButton';

const useStyles = makeStyles((theme: Theme) => {
  const totalMobileSidebarHeight = `${theme.sidebarMobileHeight +
    theme.sidebarMobilePadding * 2 +
    theme.participantBorderWidth}px`;
  return {
    container: {
      position: 'relative',
      height: '100%',
      display: 'grid',
      gridTemplateColumns: `1fr ${theme.sidebarWidth}px`,
      gridTemplateRows: '100%',
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: `100%`,
        gridTemplateRows: `calc(100% - ${totalMobileSidebarHeight}) ${totalMobileSidebarHeight}`,
      },
    },
    rightDrawerOpen: { gridTemplateColumns: `1fr ${theme.sidebarWidth}px ${theme.rightDrawerWidth}px` },
  };
});

/**
 * This hook turns on speaker view when screensharing is active, regardless of if the
 * user was already using speaker view or gallery view. Once screensharing has ended, the user's
 * view will return to whatever they were using prior to screenshare starting.
 */

export function useSetSpeakerViewOnScreenShare(
  screenShareParticipant: Participant | undefined,
  room: IRoom | null,
  setIsGalleryViewActive: React.Dispatch<React.SetStateAction<boolean>>,
  isGalleryViewActive: boolean
) {
  const isGalleryViewActiveRef = useRef(isGalleryViewActive);

  // Save the user's view setting whenever they change to speaker view or gallery view:
  useEffect(() => {
    isGalleryViewActiveRef.current = isGalleryViewActive;
  }, [isGalleryViewActive]);

  useEffect(() => {
    if (screenShareParticipant && screenShareParticipant !== room!.localParticipant) {
      // When screensharing starts, save the user's previous view setting (speaker or gallery):
      const prevIsGalleryViewActive = isGalleryViewActiveRef.current;
      // Turn off gallery view so that the user can see the screen that is being shared:
      setIsGalleryViewActive(false);
      return () => {
        // If the user was using gallery view prior to screensharing, turn gallery view back on
        // once screensharing stops:
        if (prevIsGalleryViewActive) {
          setIsGalleryViewActive(prevIsGalleryViewActive);
        }
      };
    }
  }, [screenShareParticipant, setIsGalleryViewActive, room]);
}

export default function Room() {
  const classes = useStyles();
  const { isChatWindowOpen } = useChatContext();
  const { isBackgroundSelectionOpen, room } = useVideoContext();
  const { isGalleryViewActive, setIsGalleryViewActive, userType, connectType, duration, logoutUrl } = useAppState();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const screenShareParticipant = useScreenShareParticipant();
  const { isCallEstablished } = useParticipantCount();
  const [showTimer, setShowTimer] = useState(false);
  const [showForceEndButton, setShowForceEndButton] = useState(false);

  // logoutUrlの値を確認
  useEffect(() => {
    console.log('Room component - logoutUrl debug:', {
      logoutUrl,
      userType,
      hasLogoutUrl: !!logoutUrl,
      logoutUrlLength: logoutUrl?.length,
    });
  }, [logoutUrl, userType]);

  // Here we switch to speaker view when a participant starts sharing their screen, but
  // the user is still free to switch back to gallery view.
  useSetSpeakerViewOnScreenShare(screenShareParticipant, room, setIsGalleryViewActive, isGalleryViewActive);

  // 通話確立時にタイマーを表示（viewerまたはconnect_type=joinの場合は表示しない）
  useEffect(() => {
    console.log('Timer Debug:', {
      isCallEstablished,
      showTimer,
      userType,
      connectType,
      shouldShowTimer: isCallEstablished && !showTimer && userType !== 'viewer' && connectType !== 'join',
    });

    if (isCallEstablished && !showTimer && userType !== 'viewer' && connectType !== 'join') {
      setShowTimer(true);
    } else if (!isCallEstablished || userType === 'viewer' || connectType === 'join') {
      setShowTimer(false);
    }
  }, [isCallEstablished, showTimer, userType, connectType]);

  // type=viewerかつconnect_type=joinの時に強制終了ボタンを表示
  useEffect(() => {
    const shouldShowForceEnd = userType === 'viewer' && connectType === 'join' && isCallEstablished;
    setShowForceEndButton(shouldShowForceEnd);
  }, [userType, connectType, isCallEstablished]);

  // タイマー終了時の処理
  const handleTimerEnd = async () => {
    console.log('Timer end - Debug info:', {
      logoutUrl,
      userType,
      room: room?.name,
      hasLogoutUrl: !!logoutUrl,
      isGuest: userType === 'guest',
      isProvider: userType === 'provider',
    });

    if (room) {
      try {
        // サーバーAPIを呼び出して全参加者を強制終了
        const endpoint = process.env.REACT_APP_API_ENDPOINT
          ? `${process.env.REACT_APP_API_ENDPOINT}/api/tw/force-end`
          : '/api/tw/force-end';

        console.log('Timer end - Force end API endpoint:', endpoint);
        console.log('Timer end - Force end request body:', { room: room.name });

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ room: room.name }),
        });

        console.log('Timer end - Force end response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Timer end - Force end error response:', errorText);
          throw new Error(`Failed to force end participants: ${response.status} ${errorText}`);
        }

        const result = await response.json();
        console.log('Timer end - Force end result:', result);

        // 自分も切断
        room.disconnect();

        // 切断後に適切なページに遷移
        setTimeout(() => {
          console.log('Timer end - Redirect check:', {
            logoutUrl,
            userType,
            shouldRedirectGuest: logoutUrl && userType === 'guest',
            shouldRedirectProvider: logoutUrl && userType === 'provider',
          });

          if (logoutUrl && userType === 'guest') {
            console.log('Timer end - Redirecting guest to:', logoutUrl);
            // guestの場合は元のページに戻る
            let guestLogoutUrl = logoutUrl;
            // logoutUrlが管理画面のトップページの場合は、thankyouページに遷移
            if (logoutUrl === 'https://fujikawa-liver-pro.jp.ngrok.io/') {
              // thankyouページに遷移
              guestLogoutUrl = 'https://fujikawa-liver-pro.jp.ngrok.io/liver-wear/talk/thankyou';
              console.log('Timer end - Redirecting to thankyou page:', guestLogoutUrl);
            }
            const withParamLogoutUrl = guestLogoutUrl + '?logout=manual';
            console.log('Timer end - Final guest URL:', withParamLogoutUrl);
            // 一時的に遷移を無効化してログを確認
            console.log('Timer end - MIGRATION DISABLED - Would redirect to:', withParamLogoutUrl);
            // window.location.href = withParamLogoutUrl;
          } else if (logoutUrl && userType === 'provider') {
            console.log('Timer end - Redirecting provider to:', logoutUrl);
            // 管理者側は遷移を有効にする
            window.location.href = logoutUrl;
          }
        }, 1000); // 1秒後に遷移（切断処理の完了を待つ）
      } catch (error) {
        console.error('Error force ending participants on timer end:', error);
        // エラーが発生した場合は自分だけ切断
        room.disconnect();

        // エラー時も遷移処理を実行
        setTimeout(() => {
          console.log('Timer end (error) - Redirect check:', {
            logoutUrl,
            userType,
            shouldRedirectGuest: logoutUrl && userType === 'guest',
            shouldRedirectProvider: logoutUrl && userType === 'provider',
          });

          if (logoutUrl && userType === 'guest') {
            console.log('Timer end (error) - Redirecting guest to:', logoutUrl);
            // guestの場合は元のページに戻る
            let guestLogoutUrl = logoutUrl;
            // logoutUrlが管理画面のトップページの場合は、thankyouページに遷移
            if (logoutUrl === 'https://fujikawa-liver-pro.jp.ngrok.io/') {
              // thankyouページに遷移
              guestLogoutUrl = 'https://fujikawa-liver-pro.jp.ngrok.io/liver-wear/talk/thankyou';
              console.log('Timer end (error) - Redirecting to thankyou page:', guestLogoutUrl);
            }
            const withParamLogoutUrl = guestLogoutUrl + '?logout=manual';
            console.log('Timer end (error) - Final guest URL:', withParamLogoutUrl);
            // 一時的に遷移を無効化してログを確認
            console.log('Timer end (error) - MIGRATION DISABLED - Would redirect to:', withParamLogoutUrl);
            // window.location.href = withParamLogoutUrl;
          } else if (logoutUrl && userType === 'provider') {
            console.log('Timer end (error) - Redirecting provider to:', logoutUrl);
            // 管理者側は遷移を有効にする
            window.location.href = logoutUrl;
          }
        }, 1000);
      }
    }
  };

  return (
    <div
      className={clsx(classes.container, {
        [classes.rightDrawerOpen]: isChatWindowOpen || isBackgroundSelectionOpen,
      })}
    >
      {/* 
        This ParticipantAudioTracks component will render the audio track for all participants in the room.
        It is in a separate component so that the audio tracks will always be rendered, and that they will never be 
        unnecessarily unmounted/mounted as the user switches between Gallery View and speaker View.
      */}
      <ParticipantAudioTracks />

      {isGalleryViewActive ? (
        isMobile ? (
          <MobileGalleryView />
        ) : (
          <GalleryView />
        )
      ) : (
        <>
          <MainParticipant />
          <ParticipantList />
        </>
      )}

      <ChatWindow />
      <BackgroundSelectionDialog />

      {/* カウントダウンタイマー */}
      <CountdownTimer
        duration={typeof duration === 'number' ? Math.round(duration * 60) : 300}
        onTimerEnd={handleTimerEnd}
        isVisible={showTimer}
      />

      {/* 強制終了ボタン */}
      <ForceEndButton isVisible={showForceEndButton} />
    </div>
  );
}
